import React from "react";
import type {
  ShopSectionEntity,
  ShopSectionPortfolio,
} from "../../../../../../domain/types/company-shop";
import { PREVIEW_COLORS, SECTION_COLUMN_CLASS } from "../../constants";

interface PreviewPortfolioProps {
  section: ShopSectionEntity;
  /**
   * 백엔드가 아직 sections[].portfolios 를 안 내려줄 때 쓸 대체 목록.
   * 포트폴리오 관리 화면이 쓰는 목록에서 발행된 것만 골라 넘어온다.
   */
  fallbackPortfolios: ShopSectionPortfolio[];
}

/** yyyy-MM-dd → YYYY.MM (쇼핑몰 목록이 moment 로 같은 형식을 쓴다) */
const formatWorkedAt = (workedAt?: string | null): string | null => {
  if (!workedAt) return null;
  const [year, month] = workedAt.split("-");
  if (!year || !month) return workedAt;
  return `${year}.${month}`;
};

const PortfolioThumbnail = ({
  portfolio,
  className,
}: {
  portfolio: ShopSectionPortfolio;
  className: string;
}) => (
  <div
    className={`overflow-hidden ${className}`}
    style={{
      backgroundImage: `linear-gradient(145deg, ${PREVIEW_COLORS.portfolio.thumbnailFrom} 0%, ${PREVIEW_COLORS.portfolio.thumbnailTo} 100%)`,
    }}
  >
    {portfolio.thumbnailUrl ? (
      <img
        src={portfolio.thumbnailUrl}
        alt="포트폴리오 대표 이미지."
        className="object-cover w-full h-full"
      />
    ) : null}
  </div>
);

const PortfolioInfo = ({ portfolio }: { portfolio: ShopSectionPortfolio }) => {
  const workedAt = formatWorkedAt(portfolio.workedAt);

  return (
    <>
      {portfolio.clientName ? (
        <span
          className="inline-flex w-fit text-[11px] px-[0.625rem] py-[0.25rem]
            / rounded-[4px] mb-[0.625rem] font-medium"
          style={{
            color: PREVIEW_COLORS.portfolio.summary,
            backgroundColor: PREVIEW_COLORS.portfolio.clientBackground,
          }}
        >
          {portfolio.clientName}
        </span>
      ) : null}

      <h4
        className="text-[15px] font-bold leading-[1.5] mb-[0.5rem] line-clamp-2"
        style={{ color: PREVIEW_COLORS.portfolio.title }}
      >
        {portfolio.title}
      </h4>

      {portfolio.summary ? (
        <p
          className="text-[13px] leading-[1.6] line-clamp-2"
          style={{ color: PREVIEW_COLORS.portfolio.summary }}
        >
          {portfolio.summary}
        </p>
      ) : null}

      {workedAt ? (
        <span
          className="block text-[12px] font-medium mt-[0.875rem] pt-[0.875rem] border-t"
          style={{
            color: PREVIEW_COLORS.portfolio.summary,
            borderColor: PREVIEW_COLORS.portfolio.cardBorder,
          }}
        >
          {workedAt}
        </span>
      ) : null}
    </>
  );
};

/**
 * 포트폴리오 메인 섹션 미리보기.
 *
 * 관리자가 고른 layoutType 이 방문자에게 그대로 보인다(결정 1 - 방문자
 * 토글은 만들지 않는다). 그래서 미리보기도 두 레이아웃을 실제로 그린다.
 *
 * 색·모서리·4:3 비율은 쇼핑몰 portfolio/index.css 의 실제 값을 따른다.
 * 미리보기가 다른 값을 쓰면 관리자가 보고 고른 결과와 배포된 화면이 달라진다.
 */
export const PreviewPortfolio = ({
  section,
  fallbackPortfolios,
}: PreviewPortfolioProps) => {
  /*
    백엔드 미배포 구간에서는 section.portfolios 가 빈 배열로 온다.
    그때는 포트폴리오 관리 화면에서 읽어온 목록으로 대신 그린다 -
    빈 블록만 보여주면 관리자가 레이아웃을 고를 근거가 없다.
  */
  const portfolios =
    section.portfolios.length > 0 ? section.portfolios : fallbackPortfolios;

  const isList = section.layoutType === "LIST";
  // 쇼핑몰이 `portfolio-card-grid cols-N` 으로 쓰는 값과 같은 열 수를 그린다
  const columnClass =
    SECTION_COLUMN_CLASS[section.columnCount] ?? SECTION_COLUMN_CLASS[4];

  return (
    <div className="text-start flex flex-col flex-start py-[40px] px-[32px]">
      <h2
        className="mb-[0.5rem] text-[1.125rem] font-semibold"
        style={{ color: PREVIEW_COLORS.sectionTitle }}
      >
        {section.title || "포트폴리오"}
      </h2>
      {section.subtitle ? (
        <div
          className="mb-[1.5rem]"
          style={{ color: PREVIEW_COLORS.sectionSubtitle }}
        >
          {section.subtitle}
        </div>
      ) : (
        <div className="mb-[1rem]" />
      )}

      {portfolios.length === 0 ? (
        <div
          className="py-[1.5rem] text-center"
          style={{ color: PREVIEW_COLORS.mutedText }}
        >
          쇼핑몰에 노출 중인 포트폴리오가 없습니다.
        </div>
      ) : isList ? (
        <div className="flex flex-col gap-[16px]">
          {portfolios.map((portfolio) => (
            <div
              key={portfolio.id}
              className="flex flex-row gap-[16px] / rounded-[12px] overflow-hidden border"
              style={{
                backgroundColor: PREVIEW_COLORS.portfolio.cardBackground,
                borderColor: PREVIEW_COLORS.portfolio.cardBorder,
              }}
            >
              <PortfolioThumbnail
                portfolio={portfolio}
                className="w-[9rem] shrink-0 aspect-[4/3]"
              />
              <div className="flex flex-col justify-center py-[1rem] pr-[1.25rem] min-w-0">
                <PortfolioInfo portfolio={portfolio} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`grid ${columnClass} gap-[16px] / items-start`}>
          {portfolios.map((portfolio) => (
            <div
              key={portfolio.id}
              className="w-full rounded-[12px] overflow-hidden border"
              style={{
                backgroundColor: PREVIEW_COLORS.portfolio.cardBackground,
                borderColor: PREVIEW_COLORS.portfolio.cardBorder,
              }}
            >
              {/* 작업물은 가로형이 많아 쇼핑몰이 4:3 으로 잡는다 (상품 카드의 1:1 과 다르다) */}
              <PortfolioThumbnail
                portfolio={portfolio}
                className="w-full aspect-[4/3]"
              />
              <div className="flex flex-col px-[1.25rem] pt-[1.125rem] pb-[1.25rem]">
                <PortfolioInfo portfolio={portfolio} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
